import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, MessageSquare } from 'lucide-react'
import { useSendReadyMessage } from '@/api/receipts'
import { v2ErrorText } from '@/api/v2'
import { useCustomer } from '@/api/customers'
import { useUIStore } from '@/stores/uiStore'
import { useSessionStore } from '@/stores/sessionStore'
import { buildReadySms, SMS_MAX_LENGTH } from '@/lib/readySms'

export interface CustomerMessageItem {
  name: string
  qty: number
}

export interface CustomerMessageDialogProps {
  open: boolean
  onClose: () => void
  /** 'ready' = first send (transitions kiosk_status 1 -> 2 in caller).
   *  'remind' = re-send to a kiosk_status==2 order (no transition). */
  mode: 'ready' | 'remind'
  receiptId: number
  receiptNumber: string | null | undefined
  customerId: number | null | undefined
  customerDisplayName: string | null | undefined
  /** Pre-formatted item rows, used to render the "Order Summary" bullet
   *  list in the locked template. Owner of the receipt detail (the
   *  parent OrderDetailModal) already has the items resolved with names
   *  + qtys, so we accept the projection here rather than re-deriving
   *  the per-item helpers. */
  items: CustomerMessageItem[]
  /** Delivery orders get a "ready for delivery" message instead of the
   *  pickup wording (and skip the "claim within 48 hours" pickup note). */
  isDelivery?: boolean
  /** Called after the BE confirms the message was sent. The caller is
   *  responsible for advancing kiosk_status (ready) or just closing
   *  (remind) — keeping the side-effect split makes the dialog reusable. */
  onSent: () => void | Promise<void>
  /** 'ready' mode only: advance kiosk_status without sending anything, for
   *  a customer with no reachable phone/email or an SMS that keeps being
   *  rejected. Resolves true once the order is marked ready (the caller
   *  owns the toasts); false keeps the dialog open for a retry. */
  onMarkReadyWithoutMessage?: () => Promise<boolean>
}

function formatStoreAddress(store: {
  address: string | null
  city: string | null
  province: string | null
  postal_code: string | null
} | null): string {
  if (!store) return ''
  const parts = [store.address, store.city, store.province, store.postal_code]
    .map((v) => (v ?? '').trim())
    .filter(Boolean)
  return parts.join(', ')
}

function formatItemsList(items: CustomerMessageItem[]): string {
  if (items.length === 0) return '• (no items)'
  return items
    .map((it) => {
      const qty = Number.isFinite(it.qty) ? Math.abs(it.qty) : 1
      const qtyLabel = qty === 1 ? '1' : String(qty)
      return `• ${qtyLabel}× ${it.name}`
    })
    .join('\n')
}

/** Build the locked customer-ready message body. Template is canonical
 *  per the store's branding ask — never edited by the cashier. Any future
 *  per-store override should land as a sys_config-driven swap of this
 *  function, not as an editable textarea. */
function buildMessage(args: {
  customerName: string
  orderId: string
  storeName: string
  storeAddress: string
  itemsText: string
  isDelivery?: boolean
}): string {
  const { customerName, orderId, storeName, storeAddress, itemsText, isDelivery } = args
  // Delivery orders are on their way to the customer — "ready for delivery"
  // and no pickup/claim instruction. Pickup orders keep the original wording.
  const readyLine = isDelivery
    ? `Your order ${orderId} at ${storeName || 'our store'} is ready for delivery and on its way.`
    : `Your order ${orderId} at ${storeName || 'our store'} is ready for pickup.`
  const claimLine = isDelivery
    ? ''
    : `Please claim your order within 48 hours or it will be automatically cancelled.\n\n`
  return (
    `Hi ${customerName || 'there'},\n\n` +
    `${readyLine}\n\n` +
    `Order Summary\n\n` +
    `${itemsText}\n\n` +
    claimLine +
    `${storeName}\n` +
    `${storeAddress}\n\n` +
    `Thank you,\n` +
    `The ${storeName || 'store'} team`
  )
}

export function CustomerMessageDialog({
  open,
  onClose,
  mode,
  receiptId,
  receiptNumber,
  customerId,
  customerDisplayName,
  items,
  isDelivery,
  onSent,
  onMarkReadyWithoutMessage,
}: CustomerMessageDialogProps) {
  const showToast = useUIStore((s) => s.showToast)
  const sendMessage = useSendReadyMessage()
  const selectedStore = useSessionStore((s) => s.selectedStore)

  // Pull the customer record only when this dialog is open — otherwise
  // every order-detail modal would prefetch contact info we never use.
  const { data: customer } = useCustomer(open ? customerId ?? null : null)

  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [sendSms, setSendSms] = useState(true)
  const [sendEmail, setSendEmail] = useState(true)
  const [didSeed, setDidSeed] = useState(false)

  // Full-lifecycle submit gate. `sendMessage.isPending` only covers the
  // first BE call; `await onSent()` (kiosk_status transition) runs after
  // and can be 100-400ms more. During that window the FE would otherwise
  // re-enable the Send button, the backdrop, AND the X -- the cashier
  // could close + reopen the dialog (Ready button still showing, since
  // kiosk_status hasn't flipped in the parent yet) and fire a second
  // SMS/email. A ref-only latch doesn't help because the dialog unmount
  // on close clears it, and the gates that allow the close (backdrop /
  // X) read sendMessage.isPending, not the ref.
  //
  // State (not ref) so all gates re-render when it changes. We only
  // reset to false on the FAILURE paths (BE rejection, hard error)
  // where the cashier needs to retry within the open dialog. The
  // success path lets onClose unmount the dialog; the effect below
  // resets on the next open, so we never carry an inappropriate
  // `isSubmitting=true` into a fresh open.
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Which action holds the gate, so "Mark ready" does not read "Sending…".
  const [markingReady, setMarkingReady] = useState(false)

  // Reset on close so the next open re-pulls the customer's current
  // contact info (the cashier may have edited the customer in between).
  useEffect(() => {
    if (!open) {
      setDidSeed(false)
      setPhone('')
      setEmail('')
      setSendSms(true)
      setSendEmail(true)
      setIsSubmitting(false)
      setMarkingReady(false)
    }
  }, [open])

  // Escape belongs to this dialog while it is open. Capture phase on
  // window runs ahead of the bubble-phase Escape handlers of the modals
  // under it (OrderDetailModal, CustomerModal), which would otherwise close
  // their modal and unmount this one mid-send -- past the isSubmitting
  // lock, so a reopen before kiosk_status flips could text the customer
  // twice. Same pattern as ClerkPinReauthModal.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (!isSubmitting) onClose()
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [open, isSubmitting, onClose])

  // Seed phone/email exactly once per open, after the customer record
  // resolves. Without the latch React Query re-renders would clobber
  // any cashier edit before they could hit Send.
  useEffect(() => {
    if (!open || didSeed) return
    if (customerId != null && !customer) return
    const c = customer as
      | { phone?: string | null; email?: string | null }
      | undefined
    setPhone(c?.phone ?? '')
    setEmail(c?.email ?? '')
    setDidSeed(true)
  }, [open, didSeed, customer, customerId])

  // The message body is locked: built from the receipt + store + items
  // and rendered read-only. No cashier edits, no per-mode template
  // switching today — the same body covers both first send and reminder.
  const message = useMemo(() => {
    return buildMessage({
      customerName: customerDisplayName ?? '',
      orderId: receiptNumber ? `#${receiptNumber}` : `#${receiptId}`,
      storeName: selectedStore?.name ?? '',
      storeAddress: formatStoreAddress(selectedStore),
      itemsText: formatItemsList(items),
      isDelivery,
    })
  }, [customerDisplayName, receiptNumber, receiptId, selectedStore, items, isDelivery])

  // The SMS gets its own one-segment body (see lib/readySms); the long
  // message above is the email.
  const smsMessage = useMemo(() => {
    return buildReadySms({
      customerName: customerDisplayName,
      orderNumber: receiptNumber || String(receiptId),
      storeName: selectedStore?.name,
      storeStreet: selectedStore?.address,
      isDelivery,
    })
  }, [customerDisplayName, receiptNumber, receiptId, selectedStore, isDelivery])

  const canSend =
    !isSubmitting &&
    ((sendSms && phone.trim().length > 0) ||
      (sendEmail && email.trim().length > 0))

  const handleSend = async () => {
    // Lifecycle gate: covers mutateAsync + onSent. Without this, the
    // backdrop/X re-enable as soon as the mutation resolves -- the
    // cashier could close + reopen during onSent and fire a second
    // notification before kiosk_status flips on the parent.
    if (isSubmitting) return
    setIsSubmitting(true)
    // Derive the channel flags from the populated state so the payload
    // matches what canSend approved. With both toggles on but only one
    // contact filled, canSend's OR passes but the unchecked payload
    // would still have us asking for an empty-phone SMS or empty-email
    // email -- BE either rejects the whole request or silently drops
    // the empty leg.
    const phoneTrim = phone.trim()
    const emailTrim = email.trim()
    const effectiveSendSms = sendSms && phoneTrim.length > 0
    const effectiveSendEmail = sendEmail && emailTrim.length > 0
    try {
      const result = await sendMessage.mutateAsync({
        receiptId,
        body: {
          send_sms: effectiveSendSms,
          send_email: effectiveSendEmail,
          phone: phoneTrim,
          email: emailTrim,
          // A backend without `sms_message` sends `message` as the SMS
          // too, so an SMS-only send carries the short body in both.
          message: effectiveSendEmail ? message : smsMessage,
          sms_message: smsMessage,
        },
      })
      // Defensive only: v2api throws on a {success: false, error: string}
      // body, so a refusal normally lands in the catch below.
      if (result?.success === false) {
        showToast({
          type: 'error',
          title: 'Could not send message',
          message: result.message || result.error || 'Please try again.',
        })
        // Release gate so the cashier can retry inside the still-open
        // dialog. Closing also resets via the effect above.
        setIsSubmitting(false)
        return
      }
      showToast({
        type: 'success',
        title: 'Message sent',
        message: `Customer ${effectiveSendSms && effectiveSendEmail ? 'SMS + email' : effectiveSendSms ? 'SMS' : 'email'} sent.`,
      })
      await onSent()
      onClose()
      // Success path intentionally leaves isSubmitting=true through
      // onClose so the close-and-reopen race can't run. The effect
      // above resets on the next mount.
    } catch (err) {
      // A refused SMS arrives here as a V2ApiError: the code
      // (sms_rejected) in err.message, the provider's reason -- when the
      // backend sends one -- in err.detail. v2ErrorText prefers the reason.
      showToast({
        type: 'error',
        title: 'Could not send message',
        message: v2ErrorText(err, 'Please try again.'),
      })
      // Release gate on hard failure so the cashier can retry.
      setIsSubmitting(false)
    }
  }

  const handleMarkReadyOnly = async () => {
    if (isSubmitting || !onMarkReadyWithoutMessage) return
    setIsSubmitting(true)
    setMarkingReady(true)
    let marked = false
    try {
      marked = await onMarkReadyWithoutMessage()
    } finally {
      if (marked) {
        onClose()
      } else {
        setIsSubmitting(false)
        setMarkingReady(false)
      }
    }
  }

  if (!open) return null

  const title = mode === 'remind' ? 'Send Reminder' : 'Send Customer Message'

  // Stop clicks inside the dialog from bubbling. The dialog is mounted
  // inside OrderDetailModal's tree, whose root has `onClick={onClose}`
  // as the click-outside handler — without this stop, every click on a
  // checkbox/input/button inside the dialog would tear down the parent
  // modal and unmount the dialog with it.
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isSubmitting ? undefined : onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <MessageSquare size={20} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-[auto_1fr] items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                className="rounded"
              />
              SMS
            </label>
            <div
              className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 ${
                sendSms ? 'text-gray-700' : 'text-gray-400'
              }`}
            >
              {phone || <span className="text-gray-400 italic">no phone on file</span>}
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="rounded"
              />
              Email
            </label>
            <div
              className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 ${
                sendEmail ? 'text-gray-700' : 'text-gray-400'
              }`}
            >
              {email || <span className="text-gray-400 italic">no email on file</span>}
            </div>
          </div>

          {sendSms && phone.trim() && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-gray-700">SMS preview</span>
                <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                  {smsMessage.length}/{SMS_MAX_LENGTH} · Locked
                </span>
              </div>
              <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                {smsMessage}
              </div>
            </div>
          )}

          {sendEmail && email.trim() && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-gray-700">Email preview</span>
                <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                  Locked
                </span>
              </div>
              <div className="w-full max-h-64 overflow-y-auto px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                {message}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="flex-1 py-3 font-semibold rounded-xl transition-colors cursor-pointer bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting && !markingReady ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending…
              </>
            ) : (
              'Send'
            )}
          </button>
        </div>
        {mode === 'ready' && onMarkReadyWithoutMessage && (
          <div className="px-6 pb-4 bg-gray-50 text-center">
            <button
              onClick={handleMarkReadyOnly}
              disabled={isSubmitting}
              className="text-sm font-medium text-gray-500 hover:text-gray-800 underline underline-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {markingReady ? 'Marking ready…' : 'Mark ready without sending a message'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
